import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';

import { iclApi, taskApi } from '../../api';
import { getStudents } from '../../Common/Database';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorCanvas from '../../Components/ImageEditor/EditorCanvas';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { ICL, TaskKeysType } from '../../shared/types';
import { SummarizeTask } from '../../shared/types/task';
import { useDispatch, useSelector } from 'react-redux';
import { getTotalPoint, setTotalPoint } from 'redux/slices/flagsSlice';

interface Props {
  mail: string;
  preview?: boolean;
}

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  sentenceStructure: StudentSentenceStructureTask,
  gap: StudentGapTask,
  mathFormulas: StudentMathFormulasTask,
  wordComposition: StudentWordCompositionTask,
  wordStress: StudentWordStressTask,
  underline: StudentUnderlineTask,
};

interface LocationState {
  state: State;
}

interface State {
  taskId?: string;
  checkListId?: string;
}

export default function ChecklistTeacherNew({ mail, preview }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  // on this page we render exams and icls for teacher to check them

  const dispatch = useDispatch();
  const { state } = useLocation() as LocationState;

  const [iclData, setIclData] = useState<ICL | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);
  const isExam = iclData?.type === 'exam';
  const workTitle = isExam ? 'examlist' : 'checklist';

  const getTotalPointFromTasks = () => {
    return tasks.filter(task => task.isCorrectAnswer).reduce((total, task) => total + (task.points || 0), 0);
  };

  useEffect(() => {
    dispatch(setTotalPoint(getTotalPointFromTasks()));
  }, [tasks]);

  const [studentName, setStudentName] = useState<string>('');

  const taskId = state?.taskId;
  const checkListId = state?.checkListId;
  const taskRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (checkListId) {
      setLoading(true);

      iclApi.getICL(checkListId).then(data => {
        setIclData(data);

        setLoading(false);
      });
    }
  }, []);

  useEffect(() => {
    if (iclData) {
      getStudents().then(students => {
        const student = students.find(s => s.email?.replace(/\./g, '') === iclData.studentEmail);
        if (student) {
          setStudentName(student.FIO);
        }
      });
    }
  }, [iclData]);

  useEffect(() => {
    if (checkListId) {
      const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: checkListId, setNewTasks: updateTasks });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  useEffect(() => {
    const scrollToTask = () => {
      if (taskId && taskRefs.current[taskId]) {
        taskRefs.current[taskId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const interval = setInterval(scrollToTask, 100);
    setTimeout(() => clearInterval(interval), 2000);

    return () => clearInterval(interval);
  }, [taskId]);

  const handleChangeTaskByKey = (id: string, key: TaskKeysType, value: string[] | string | boolean) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };

    newTaskArray.splice(index, 1, updatedObject);

    if (checkListId) {
      if (updatedObject.checkedAt || !updatedObject.checkedAt) {
        taskApi.updateTask<SummarizeTask>(checkListId, updatedObject);
      }
    }

    setTasks(newTaskArray);
  };

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    if (state.checkListId) {
      await taskApi.updateTask<SummarizeTask>(state.checkListId, updatedObject).then(() => setLoading(false));
    }
  };

  const renderTasks = () => {
    return tasks.map((it: SummarizeTask, index: number) => {
      const Component = componentMapping[it.type];

      return (
        <div key={it.id} ref={el => (taskRefs.current[it.id] = el)}>
          <Component
            studentName={studentName}
            icl={iclData}
            index={index}
            {...it}
            handleChangeTaskByKey={handleChangeTaskByKey}
            saveTaskAnswer={saveTaskAnswer}
            preview={preview}
          />
        </div>
      );
    });
  };

  return (
    <EditorProvider>
      <LoadingError isLoading={isLoading && !tasks}>
        <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
          <div className="flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
            <div className="lg:leading-12 text-[24px] font-bold leading-9 lg:text-[48px]">
              {t(workTitle)} №<span>{iclData?.number}</span>
            </div>

            <div className="text-[20px] font-bold leading-7 lg:text-[32px] lg:leading-10">
              {t('Cource name')}: <span className="font-medium">{iclData?.courseName}</span>
            </div>

            <div className="text-[18px] font-bold leading-6 lg:text-[24px] lg:leading-8">
              {t('theme')}: <span className="font-medium">{iclData?.theme}</span>
            </div>

            <div className="flex flex-col gap-[12px] md:flex-row md:gap-[30%] lg:flex-row lg:gap-[30%]">
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('subject')}:{' '}
                <span className="font-medium">
                  {' '}
                  {platformLang === 'ua' ? iclData?.subject.name : iclData?.subject.ruName}
                </span>
              </div>

              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('language')}: <span className="font-medium">{iclData?.language}</span>
              </div>

              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('class')}: <span className="font-medium">{t(iclData?.class)}</span>
              </div>
            </div>

            {studentName && (
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('Student')}: <span className="font-medium">{studentName}</span>
              </div>
            )}
          </div>

          <EditorCanvas icl={iclData} handleChangeTaskByKey={handleChangeTaskByKey} />

          <div>{renderTasks()}</div>
        </div>
      </LoadingError>
    </EditorProvider>
  );
}
